<template>
    <div>
        <div class="d-flex align-center mb-1">
            <v-text-field
                v-model.trim="search_query"
                :label="$t('views.main.drafts.drafts.search_label')"
                prepend-inner-icon="mdi-magnify"
                clearable
            ></v-text-field>

            <v-spacer></v-spacer>

            <span class="body-2 text--secondary d-none d-sm-inline">
                {{ from }}-{{ to }} {{ $t('views.main.drafts.drafts.pagination_of') }} {{ total }}
            </span>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="is_page_first" @click="prev_page">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.drafts.drafts.pagination_prev_page') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon v-bind="attrs" v-on="on" :disabled="is_page_last" @click="next_page">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('views.main.drafts.drafts.pagination_next_page') }}</span>
            </v-tooltip>
        </div>

        <loading-message v-if="loading" :message="loading_message" class="my-5"></loading-message>

        <v-simple-table v-else-if="drafts.length">
            <template v-slot:default>
                <tbody>
                <router-link
                    :to="{name: 'draft_edit', params: { id: draft.DraftID }}"
                    tag="tr"
                    v-for="draft in drafts"
                    :key="draft.DraftName"
                    style="cursor: pointer"
                >
                    <th class="text-no-wrap">
                        {{ draft.DraftName }}
                        <template v-if="draft.CustomerName">
                            <div class="font-weight-regular text-caption text--secondary">
                                {{draft.CustomerName}}
                            </div>
                        </template>
                    </th>
                    <td>
                        <v-chip v-for="system in draft.Systems" :key="'system'+system.SystemID" x-small color="info" class="my-1 mr-1">{{ system.SystemName }}</v-chip>
                    </td>
                    <td class="text-no-wrap"><!-- {{ draft.DraftPrice | currencyFormat }} --></td>
                    <td class="secondary--text">{{ $t('views.main.drafts.drafts.draft') }}</td>
                    <td class="text-no-wrap">{{ draft.CreatedAt | dateConvenientFormat }}</td>
                    <td class="text-right">

                        <v-menu
                            bottom
                            left
                            :offset-y="true"
                            rounded
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    small
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>

                            <v-list dense>
                                <v-list-item>
                                    <v-list-item-title>
                                        <router-link :to="{name: 'draft_edit', params: { id: draft.DraftID }}">
                                            <v-icon small>mdi-clipboard-edit-outline</v-icon>
                                            {{ $t('views.main.drafts.drafts.edit') }}
                                        </router-link>
                                    </v-list-item-title>
                                </v-list-item>

                                <v-list-item>
                                    <v-list-item-title>
                                        <a @click="getDraftPdfOffer(draft)">
                                            <v-icon small>mdi-file-download-outline</v-icon>
                                            {{ $t('views.main.drafts.drafts.get_offer') }}
                                        </a>
                                    </v-list-item-title>
                                </v-list-item>

                                <v-list-item>
                                    <v-list-item-title>
                                        <a @click="showDeleteDialog(draft)">
                                            <v-icon small>mdi-delete-outline</v-icon>
                                            {{ $t('views.main.drafts.drafts.delete') }}
                                        </a>
                                    </v-list-item-title>
                                </v-list-item>

                            </v-list>
                        </v-menu>

                    </td>
                </router-link>
                </tbody>
            </template>
        </v-simple-table>

        <v-alert
            v-else
            border="left"
            colored-border
            type="info"
            elevation="1"
        >{{ empty_message }}</v-alert>

        <v-dialog
            v-model="draft_delete_dialog"
            persistent
            width="500"
        >
            <v-card v-if="draft_to_delete">
                <v-card-title class="headline grey lighten-2 mb-5">
                    {{ $t('views.main.drafts.drafts.draft_removal') }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="hideDeleteDialog"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text>
                    {{ $t('views.main.drafts.drafts.sure_to_remove_draft_with_number') }}
                    <strong>{{ draft_to_delete ? draft_to_delete.DraftName : null }}</strong>?
                    {{ $t('views.main.drafts.drafts.operation_is_irreversible') }}
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="error"
                        rounded
                        @click="deleteDraft(draft_to_delete)"
                        :loading="deleting"
                    >
                        {{ $t('views.main.drafts.drafts.delete_button') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import LoadingMessage from "../../../components/LoadingMessage";
    import {Paths} from "../../../modules/utils";

    export default {
        name: "Drafts",

        components: {
            LoadingMessage,
        },

        data() {
            return {
                search_query: "",
                source: null,

                loading: false,
                deleting: false,

                draft_delete_dialog: false,
                draft_to_delete: null,

                drafts: [],

                // pagination
                page: 1,
                last_page:1,
                from: 0,
                to: 0,
                total: 0,
            }
        },

        computed: {
            is_page_first() {
                return this.page === 1;
            },
            is_page_last() {
                return this.page === this.last_page;
            },
            search() {
                return this.search_query;
            },
            loading_message() {
                return ! this.search
                    ? this.$t('views.main.drafts.drafts.loading_drafts')
                    : this.$t('views.main.drafts.drafts.searching_drafts');
            },
            empty_message() {
                return ! this.search
                    ? this.$t('views.main.drafts.drafts.no_drafts')
                    : this.$t('views.main.drafts.drafts.no_drafts_matching_query');
            },
            locale: function () {
                return this.$i18n.locale;
            },
        },

        methods: {
            next_page: function () {
                if(this.page < this.last_page) this.page++;
            },
            prev_page: function () {
                if(this.page > 1) this.page--;
            },

            fetch: function () {
                this.loading = true;

                this.$store.dispatch('loadDrafts', {
                    page: this.page,
                    search_query: this.search_query
                }).then(response => {
                    if(response.status === 200) {
                        this.drafts = response.data.data;
                        this.page = response.data.meta.current_page ?? 1;
                        this.last_page = response.data.meta.last_page ?? 1;
                        this.from = response.data.meta.from ?? 0;
                        this.to = response.data.meta.to ?? 0;
                        this.total = response.data.meta.total;
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.drafts.loading_drafts_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.loading = false;
                    });
            },
            deleteDraft: function (draft) {
                this.deleting = true;

                this.$store.dispatch('destroyDraft', draft.DraftID).then(response => {
                    if(response.status === 204) {
                        this.hideDeleteDialog();
                        this.$store.commit(
                            "showSnackbarSuccess",
                            this.$t('views.main.drafts.drafts.draft_deleted')
                        );
                        this.fetch();
                    } else {
                        this.$store.commit(
                            "showSnackbarError",
                            this.$t('views.main.drafts.drafts.draft_deleting_failed')
                        );
                    }
                })
                    .catch(() => {})
                    .then(() => {
                        this.deleting = false;
                    });
            },
            showDeleteDialog: function (draft) {
                this.draft_to_delete = draft;
            },
            hideDeleteDialog: function () {
                this.draft_delete_dialog = false;
            },
            getDraftPdfOffer: function (draft) {
                location.href = Paths.pdfOffer(draft.DraftID);
            },
        },

        created() {
            this.debouncedFetch = _.debounce(this.fetch, 900);
            this.debouncedFetchFast = _.debounce(this.fetch, 300);

            this.fetch();
        },

        watch: {
            search_query: function () {
                //If any request_search is currently in progress, cancel it!
                if(this.$store.state.sources.load_drafts) {
                    this.$store.state.sources.load_drafts.cancel();
                }

                //Run debouncedFetch
                this.debouncedFetch();
            },
            page: function () {
                //If any request_search is currently in progress, cancel it!
                if(this.$store.state.sources.load_drafts) {
                    this.$store.state.sources.load_drafts.cancel();
                }

                //Run debouncedFetch
                this.debouncedFetchFast();
            },
            draft_to_delete: function (new_value) {
                if(new_value !== null) {
                    this.draft_delete_dialog = true;
                }
            },
            draft_delete_dialog: function (new_value) {
                if(!new_value) {
                    this.draft_to_delete = null;
                }
            },
            locale() {
                this.fetch();
            },
        }
    }
</script>

<style scoped>

</style>
